import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useStorefrontProduct - composable for fetching a public storefront
 * product detail by (store slug, product id) from the backend
 * (`GET /api/storefront/<slug>/products/<id>`).
 *
 * Powers the mount-time fetch on the storefront Product page
 * (`pages/store/ProductPage.vue`, the P6 surface at
 * `/shop/:slug/c/:categorySlug/p/:id`).
 *
 * History: this originally served the flat Phase 2-B detail page
 * `StoreProductDetail.vue` at `/store/:slug/products/:id`. That surface was
 * retired by P8 (storefront convergence, card q7EQ3a0b) and DELETED as dead
 * code; `ProductPage.vue` is now the only consumer.
 *
 * Public surface (locked contract):
 *
 *   const { product, loading, error, fetchProduct } = useStorefrontProduct()
 *
 *   product: ref<Object|null>     - last successful payload, shape:
 *                                     { id, name, description, price_cents,
 *                                       hero_image_url, colors:[{id,name,hex}],
 *                                       sizes:[{id,label,price_cents}],
 *                                       campaign_id, store_name }
 *
 *   ``sizes[].price_cents`` is the PER-SIZE price and is the source of
 *   truth for pricing on the product page. The top-level ``price_cents``
 *   is a size-blind "from" price and is WRONG for any upcharged size --
 *   quoting it on a 2XL under-charged the shopper by $11.00 on campaign
 *   product 128 (fixed 2026-08-28). This docstring previously documented
 *   the shape as ``sizes:[{id,label}]``, which the endpoint has not
 *   emitted for some time. Verified against
 *   ``GET /api/storefront/acmegolf/products/128``.
 *   loading: ref<boolean>         - request in flight
 *   error:   ref<{status,message}|null>
 *   fetchProduct(slug, id): Promise<void>
 *
 * Behavior notes:
 *   - Concurrent fetches are de-duped via AbortController on the prior
 *     request and a monotonic generation counter on returned responses.
 *     Late stale responses are silently discarded. Mirrors the pattern
 *     used by useSanmarProductMeta() so future maintainers see one
 *     idiom across the codebase.
 *   - `product` is cleared synchronously on a new fetch so a stale
 *     payload never bleeds across navigations to a different product.
 *   - `error` IS cleared at the start of each fetch.
 *   - 404 messages are user-correctable ("product is no longer available")
 *     vs generic ("failed to load product") so the detail page can
 *     show the right retry affordance.
 */
export function useStorefrontProduct() {
    const product = ref(null)
    const loading = ref(false)
    const error = ref(null)

    let generation = 0
    let currentAbort = null

    const fetchProduct = async (slug, id) => {
        if (slug === undefined || slug === null || slug === '') {
            error.value = { status: 0, message: 'Missing store identifier' }
            product.value = null
            loading.value = false
            return
        }
        if (id === undefined || id === null || id === '') {
            error.value = { status: 0, message: 'Missing product identifier' }
            product.value = null
            loading.value = false
            return
        }

        if (currentAbort) {
            currentAbort.abort()
        }

        const myGeneration = ++generation
        const controller =
            typeof AbortController !== 'undefined'
                ? new AbortController()
                : null
        currentAbort = controller

        product.value = null
        loading.value = true
        error.value = null

        try {
            const response = await axiosInstance.get(
                `/api/storefront/${encodeURIComponent(slug)}/products/${encodeURIComponent(id)}`,
                controller ? { signal: controller.signal } : undefined
            )

            if (myGeneration !== generation) return

            product.value = response.data
            error.value = null
        } catch (err) {
            if (myGeneration !== generation) return

            const isAbort =
                err?.name === 'CanceledError' ||
                err?.name === 'AbortError' ||
                err?.code === 'ERR_CANCELED'
            if (isAbort) return

            const status = err?.response?.status ?? 0
            error.value = mapError(status)
            product.value = null
        } finally {
            if (myGeneration === generation) {
                loading.value = false
                currentAbort = null
            }
        }
    }

    return { product, loading, error, fetchProduct }
}

/**
 * Map an HTTP status into the user-facing error shape. 404 is
 * user-correctable (product was removed or never existed) - the page
 * shows a "this product is no longer available" message and a link
 * back to the gallery. Other errors are likely transient and the
 * page offers a retry button.
 */
function mapError(status) {
    if (status === 404) {
        return {
            status: 404,
            message: 'This product is no longer available.',
        }
    }
    return {
        status,
        message: 'Failed to load product. Please try again.',
    }
}
